import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { ElasticTabsComponent } from '../elastic-tabs.component';
import {
    ACTIVE_TAB_VIEW,
    ElasticTabConfig,
    OaPerson,
} from '../models/oa.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { TeamOAService } from '../services/team-oa.service';

@Component({
    selector: 'cmps-team-view-tabs',
    template: `
        <elastic-tabs
            [input]="activeView"
            [tabsconfig]="tabsConfig"
            [inputclass]="'nav-performers'"
            #elasticTabs
            (resized)="resizedHandler($event)"
        >
            <ng-template
                #teamcountTpl
                let-default
            >
                <small
                    class="performer-count"
                    [ngStyle]="{
                        'font-size': teamcount > 99 ? '0.6rem' : '0.8rem'
                    }"
                >
                    {{ teamcount }}
                </small>
            </ng-template>
            <ng-template
                #othercountTpl
                let-default
            >
                <small
                    class="performer-count"
                    [ngStyle]="{
                        'font-size': othercount > 99 ? '0.6rem' : '0.8rem'
                    }"
                >
                    {{ othercount }}
                </small>
            </ng-template>
        </elastic-tabs>
    `,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    // templateUrl: './team-view-tabs.component.html',
})
export class TeamViewTabsComponent implements OnInit {
    @Input() activeView: ACTIVE_TAB_VIEW;
    @Input() selectedUser: any;
    @Input() othercount: number;
    @Input() teamcount: number;
    @Input() self: OaPerson;

    @Output() toggledTab: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('teamcountTpl', { static: true }) teamcountTpl: TemplateRef<any>;
    @ViewChild('othercountTpl', { static: true })
    othercountTpl: TemplateRef<any>;

    @ViewChild('elasticTabs', { static: true })
    elasticTabs: ElasticTabsComponent;

    ACTIVE_TAB_VIEW = ACTIVE_TAB_VIEW;

    destroyedComponent = false;
    i18n = TranslationKeyMap;

    constructor(
        private readonly _coachInsightService: CoachingInsightWidgetService,
        private readonly _teamOAService: TeamOAService,
        // protected _cdRef: ChangeDetectorRef,
        private readonly _translateService: TranslateService,
    ) {}

    public tabsConfig: ElasticTabConfig[] = [];

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        if (this.destroyedComponent) {
            // this._cdRef.reattach();
        }
        this.destroyedComponent = false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterContentInit() {
        this.initializeTabList();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(args) {
        // eslint-disable-next-line no-prototype-builtins
        if (args.hasOwnProperty('selectedUser')) {
            this.initializeTabList();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resizedHandler(state) {
        const x = this._teamOAService.getActiveView();

        if (state === true) {
            const f = this.tabsConfig.findIndex(
                (f) => f.activeConditionComparator === x,
            );
            if (f !== -1) {
                const g = this.tabsConfig[f];
                this.tabsConfig.splice(f, 1);
                this.tabsConfig.unshift(g);
            }
        } else {
            this.tabsConfig.sort(function (x, y) {
                return x.index - y.index;
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initializeTabList() {
        this.tabsConfig = [
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            <ElasticTabConfig>{
                liclass: 'nav-item-1',
                text: this._translateService.instant(
                    TranslationKeyMap._7442_SELF,
                ),
                activeConditionComparator: ACTIVE_TAB_VIEW.SELF,
                clickHandler: this.setView.bind(this),
                clickHandlerParams: [ACTIVE_TAB_VIEW.SELF],
                isDisabled: this.isDisabledSelf.bind(this),
                index: 0,
                enabled: true,
            },
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            <ElasticTabConfig>{
                liclass: 'nav-item-2',
                text: this._translateService.instant(
                    TranslationKeyMap._7443_TEAM,
                ),
                activeConditionComparator: ACTIVE_TAB_VIEW.TEAM,
                clickHandler: this.setView.bind(this),
                clickHandlerParams: [ACTIVE_TAB_VIEW.TEAM],
                sideTemplateRef: this.teamcountTpl,
                sideContext: { teamcount: this.teamcount },
                isDisabled: this.isDisabled.bind(this),
                index: 1,
                enabled: true,
            },
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            <ElasticTabConfig>{
                liclass: 'nav-item-3',
                text: this._translateService.instant(
                    TranslationKeyMap._7444_OTHERS,
                ),
                activeConditionComparator: ACTIVE_TAB_VIEW.OTHERS,
                clickHandler: this.setView.bind(this),
                clickHandlerParams: [ACTIVE_TAB_VIEW.OTHERS],
                sideTemplateRef: this.othercountTpl,
                sideContext: { othercount: this.othercount },
                index: 2,
                enabled: true,
            },
        ];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setView(view: ACTIVE_TAB_VIEW) {
        if (this.selectedUser) {
            this._teamOAService.setView(
                view,
                this.selectedUser.userId || this.selectedUser.value,
            );
        }

        this.toggledTab.emit(view);

        // only reorder tabs if wrapped
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.elasticTabs && this.elasticTabs.wrapped) {
            this.resizedHandler(true);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    isDisabled() {
        // it is assumed that TEAMCOUNT will include the USER THEMSELVES
        // adjust to only point to subordinates if done in backend
        return !this.teamcount;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    isDisabledSelf() {
        return this.self === null || this.self === undefined;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        // this._cdRef.detach();
        this.destroyedComponent = false;
    }
}
